import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teacherApi } from '../../api/teacher';
import { analyticsApi } from '../../api/analytics';
import { clearGameSession } from '../../api/client';
import {
  Users, Plus, Trophy, Settings, Pause, Play, RotateCcw, Check, X,
  BarChart3, TrendingUp, AlertTriangle, Download, Activity, BookOpen, LogOut,

} from 'lucide-react';

type TeacherTab = 'roster' | 'topics' | 'engagement' | 'events' | 'flagged' | 'exports';

export default function TeacherDashboard() {
  const [newUniverseName, setNewUniverseName] = useState('');
  const [newUniverseCapacity, setNewUniverseCapacity] = useState(50);
  const [selectedUniverse, setSelectedUniverse] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TeacherTab>('roster');
  const [eventLimit, setEventLimit] = useState(50);
  const queryClient = useQueryClient();

  const { data: universesData, isLoading: loadingUniverses } = useQuery({
    queryKey: ['teacher', 'universes'],
    queryFn: () => teacherApi.getUniverses(),
  });

  const createUniverse = useMutation({
    mutationFn: (name: string) => teacherApi.createUniverse(name, newUniverseCapacity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'universes'] });
      setNewUniverseName('');
    },
  });

  const { data: rosterData } = useQuery({
    queryKey: ['teacher', 'roster', selectedUniverse],
    queryFn: () => teacherApi.getRoster(selectedUniverse!),
    enabled: !!selectedUniverse,
  });

  const { data: leaderboardData } = useQuery({
    queryKey: ['teacher', 'leaderboard', selectedUniverse],
    queryFn: () => teacherApi.getLeaderboard(selectedUniverse!),
    enabled: !!selectedUniverse,
  });

  const { data: topicsData, isLoading: loadingTopics } = useQuery({
    queryKey: ['teacher', 'topics', selectedUniverse],
    queryFn: () => analyticsApi.getTopicSummary(selectedUniverse!, 30),
    enabled: !!selectedUniverse && activeTab === 'topics',
  });

  const { data: engagementData, isLoading: loadingEngagement } = useQuery({
    queryKey: ['teacher', 'engagement', selectedUniverse],
    queryFn: () => analyticsApi.getEngagementTrends(selectedUniverse!, 30),
    enabled: !!selectedUniverse && activeTab === 'engagement',
  });

  const { data: creditTrendData } = useQuery({
    queryKey: ['teacher', 'creditTrends', selectedUniverse],
    queryFn: () => analyticsApi.getCreditTrends(selectedUniverse!, 30),
    enabled: !!selectedUniverse && activeTab === 'engagement',
  });

  const { data: progressionData } = useQuery({
    queryKey: ['teacher', 'progression', selectedUniverse],
    queryFn: () => analyticsApi.getPlanetProgression(selectedUniverse!),
    enabled: !!selectedUniverse && activeTab === 'engagement',
  });

  const { data: eventsData, isLoading: loadingEvents } = useQuery({
    queryKey: ['teacher', 'events', selectedUniverse, eventLimit],
    queryFn: () => analyticsApi.getAssessmentEvents(selectedUniverse!, { limit: eventLimit, days: 30 }),
    enabled: !!selectedUniverse && activeTab === 'events',
  });

  const { data: flaggedData, isLoading: loadingFlagged } = useQuery({
    queryKey: ['teacher', 'flagged', selectedUniverse],
    queryFn: () => analyticsApi.getFlaggedActivity(selectedUniverse!, 7),
    enabled: !!selectedUniverse && activeTab === 'flagged',
  });

  const pauseUniverse = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => teacherApi.pauseUniverse(id, paused),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teacher', 'universes'] }),
  });

  const resetPlanet = useMutation({
    mutationFn: (planetId: string) => teacherApi.resetPlanet(planetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teacher', 'roster'] }),
  });

  const moderatePlanet = useMutation({
    mutationFn: ({ planetId, approved }: { planetId: string; approved: boolean }) => teacherApi.moderatePlanet(planetId, approved),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['teacher', 'roster'] }),
  });

  const universes = universesData?.data || [];
  const roster = rosterData?.data || [];
  const leaderboard = leaderboardData?.data || [];
  const topics = topicsData?.data || [];
  const engagement = engagementData?.data || [];
  const creditTrends = creditTrendData?.data || [];
  const progression = progressionData?.data || [];
  const events = eventsData?.data || [];
  const flagged = flaggedData?.data;

  const selectedUniverseName = universes.find((u) => u.universe_id === selectedUniverse)?.name;

  const handleExportEngagement = async () => {
    if (!selectedUniverse) return;
    const res = await analyticsApi.exportEngagementCsv(selectedUniverse, 30);
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engagement-${selectedUniverse}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportAssessments = async () => {
    if (!selectedUniverse) return;
    const res = await analyticsApi.exportAssessmentsCsv(selectedUniverse, 30);
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessments-${selectedUniverse}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="px-6 py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between">
        <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
          Teacher Dashboard
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Class Management</span>
          <button
            onClick={() => {
              clearGameSession();
              window.location.href = '/';
            }}
            className="flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
          >
            <LogOut className="h-3.5 w-3.5" />
            Logout
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-slate-900 border-r border-slate-800 min-h-screen p-4">
          <div className="mb-6">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">My Classes</h2>
            {loadingUniverses ? (
              <div className="text-sm text-slate-400">Loading...</div>
            ) : universes.length === 0 ? (
              <div className="text-sm text-slate-500">No classes yet</div>
            ) : (
              <div className="space-y-2">
                {universes.map((u) => (
                  <button
                    key={u.universe_id}
                    onClick={() => {
                      setSelectedUniverse(u.universe_id);
                      setActiveTab('roster');
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedUniverse === u.universe_id
                        ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-600/30'
                        : 'hover:bg-slate-800 text-slate-300'
                    }`}
                  >
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Users className="w-3 h-3" />
                      {u._count?.planets || 0}/{u.settings?.capacity || u.settings?.starMap?.reservedSlots || 50} planets
                      {u.paused && <span className="text-yellow-500 ml-1">• Paused</span>}
                    </div>
                    {(u.join_code || u.settings?.joinCode) && (
                      <div className="mt-1 text-[10px] font-mono text-cyan-300">Code {u.join_code || u.settings?.joinCode}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Create Class</h2>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Class name"
                value={newUniverseName}
                onChange={(e) => setNewUniverseName(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <select
                value={newUniverseCapacity}
                onChange={(e) => setNewUniverseCapacity(Number(e.target.value))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {[50, 100, 150, 200].map((capacity) => (
                  <option key={capacity} value={capacity}>{capacity} planet universe</option>
                ))}
              </select>
              <button
                onClick={() => newUniverseName.trim() && createUniverse.mutate(newUniverseName.trim())}
                disabled={createUniverse.isPending || !newUniverseName.trim()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          {!selectedUniverse ? (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Select a class from the sidebar to view details
            </div>
          ) : (
            <div className="space-y-6">
              {/* Class header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{selectedUniverseName}</h2>
                  <p className="text-slate-400 text-sm">
                    {roster.length}/{universes.find((u) => u.universe_id === selectedUniverse)?.settings?.capacity || 50} planets enrolled
                    {(universes.find((u) => u.universe_id === selectedUniverse)?.join_code || universes.find((u) => u.universe_id === selectedUniverse)?.settings?.joinCode) && (
                      <span className="ml-2 font-mono text-cyan-300">
                        Code {universes.find((u) => u.universe_id === selectedUniverse)?.join_code || universes.find((u) => u.universe_id === selectedUniverse)?.settings?.joinCode}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  {universes.find((u) => u.universe_id === selectedUniverse)?.paused ? (
                    <button
                      onClick={() => pauseUniverse.mutate({ id: selectedUniverse, paused: false })}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Resume
                    </button>
                  ) : (
                    <button
                      onClick={() => pauseUniverse.mutate({ id: selectedUniverse, paused: true })}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Pause className="w-4 h-4" />
                      Pause
                    </button>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-slate-800 pb-px overflow-x-auto">
                {[
                  { key: 'roster' as TeacherTab, label: 'Roster', icon: Users },
                  { key: 'topics' as TeacherTab, label: 'Topics', icon: BookOpen },
                  { key: 'engagement' as TeacherTab, label: 'Engagement', icon: TrendingUp },
                  { key: 'events' as TeacherTab, label: 'Events', icon: Activity },
                  { key: 'flagged' as TeacherTab, label: 'Flagged', icon: AlertTriangle },
                  { key: 'exports' as TeacherTab, label: 'Exports', icon: Download },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-cyan-500 text-cyan-400'
                        : 'border-transparent text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Roster tab */}
              {activeTab === 'roster' && (
                <div className="space-y-6">
                  {/* Leaderboard */}
                  {leaderboard.length > 0 && (
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        <h3 className="font-semibold">Class Leaderboard</h3>
                      </div>
                      <div className="space-y-2">
                        {leaderboard.map((entry, index) => (
                          <div
                            key={entry.user_id}
                            className="flex items-center justify-between px-3 py-2 bg-slate-800/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                                index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                                index === 1 ? 'bg-slate-400/20 text-slate-300' :
                                index === 2 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-slate-800 text-slate-500'
                              }`}>
                                {index + 1}
                              </span>
                              <div>
                                <div className="text-sm font-medium">{entry.name}</div>
                                <div className="text-xs text-slate-400">{entry.planet_name} • {entry.factionGoal || `Stage ${entry.stage}`}</div>
                              </div>
                            </div>
                            <div className="text-sm font-mono font-semibold text-yellow-400">
                              {entry.score || 0} pts
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Roster table */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                      <h3 className="font-semibold">Student Roster</h3>
                      <Settings className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-800 text-slate-400">
                            <th className="text-left px-4 py-2 font-medium">Student</th>
                            <th className="text-left px-4 py-2 font-medium">Planet</th>
                            <th className="text-left px-4 py-2 font-medium">Stage</th>
                            <th className="text-left px-4 py-2 font-medium">Credits</th>
                            <th className="text-left px-4 py-2 font-medium">Upgrades</th>
                            <th className="text-left px-4 py-2 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roster.map((entry) => (
                            <tr key={entry.user.user_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                              <td className="px-4 py-3">
                                <div className="font-medium">{entry.user.name}</div>
                                <div className="text-xs text-slate-500">{entry.user.email}</div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className={entry.planet.name_approved ? '' : 'text-yellow-400'}>
                                    {entry.planet.name}
                                  </span>
                                  {!entry.planet.name_approved && (
                                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">Pending</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 bg-slate-800 rounded text-xs">{entry.planet.stage}</span>
                              </td>
                              <td className="px-4 py-3 font-mono text-yellow-400">
                                {entry.balance.current_balance}
                              </td>
                              <td className="px-4 py-3">{entry.planet.upgrade_count}</td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1">
                                  {!entry.planet.name_approved && (
                                    <button
                                      onClick={() => moderatePlanet.mutate({ planetId: entry.planet.planet_id, approved: true })}
                                      className="p-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded"
                                      title="Approve name"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => resetPlanet.mutate(entry.planet.planet_id)}
                                    className="p-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded"
                                    title="Reset planet"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                  {entry.planet.name_approved && (
                                    <button
                                      onClick={() => moderatePlanet.mutate({ planetId: entry.planet.planet_id, approved: false })}
                                      className="p-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded"
                                      title="Reject name"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Topics tab */}
              {activeTab === 'topics' && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-cyan-400" />
                      <h3 className="font-semibold">Topic Performance (Last 30 Days)</h3>
                    </div>
                    <span className="text-xs text-slate-500">{topics.length} topics</span>
                  </div>
                  {loadingTopics ? (
                    <div className="p-8 text-center text-slate-400">Loading topic data...</div>
                  ) : topics.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">No assessment data for this class yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-800 text-slate-400">
                            <th className="text-left px-4 py-2 font-medium">Subject</th>
                            <th className="text-left px-4 py-2 font-medium">Topic</th>
                            <th className="text-left px-4 py-2 font-medium">Attempts</th>
                            <th className="text-left px-4 py-2 font-medium">Avg Score</th>
                            <th className="text-left px-4 py-2 font-medium">Avg Time</th>
                            <th className="text-left px-4 py-2 font-medium">Credits</th>
                            <th className="text-left px-4 py-2 font-medium">Misconceptions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topics.map((t, i) => (
                            <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                              <td className="px-4 py-3 text-slate-300">{t.subject || '—'}</td>
                              <td className="px-4 py-3 font-medium">{t.topic || '—'}</td>
                              <td className="px-4 py-3">{t.attempts}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${
                                        (t.avg_score_pct || 0) >= 70 ? 'bg-green-500' :
                                        (t.avg_score_pct || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(100, t.avg_score_pct || 0)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-mono">{t.avg_score_pct ?? 0}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-slate-400">{t.avg_time_sec ? `${Math.round(t.avg_time_sec / 60)}m ${t.avg_time_sec % 60}s` : '—'}</td>
                              <td className="px-4 py-3 text-yellow-400">{t.total_credits ?? 0}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {(t.misconception_tags || []).slice(0, 3).map((tag, j) => (
                                    <span key={j} className="text-[10px] bg-red-900/30 text-red-400 px-1.5 py-0.5 rounded">
                                      {tag}
                                    </span>
                                  ))}
                                  {(t.misconception_tags || []).length > 3 && (
                                    <span className="text-[10px] text-slate-500">+{(t.misconception_tags || []).length - 3}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Engagement tab */}
              {activeTab === 'engagement' && (
                <div className="space-y-6">
                  {/* Stage distribution */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-cyan-400" />
                      Planet Stage Distribution
                    </h3>
                    {progression.length === 0 ? (
                      <div className="text-sm text-slate-500">No data</div>
                    ) : (
                      <div className="flex items-end gap-4 h-32">
                        {progression.map((p) => (
                          <div key={p.stage} className="flex-1 flex flex-col items-center gap-1">
                            <div className="text-xs font-mono text-slate-400">{p.count}</div>
                            <div
                              className="w-full bg-cyan-600/40 rounded-t"
                              style={{ height: `${Math.max(8, (p.count / Math.max(...progression.map((x) => x.count))) * 100)}px` }}
                            />
                            <div className="text-xs text-slate-300">Stage {p.stage}</div>
                            <div className="text-[10px] text-slate-500">{p.avg_credits ?? 0} avg cr</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Daily engagement */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800">
                      <h3 className="font-semibold">Daily Engagement (Last 30 Days)</h3>
                    </div>
                    {loadingEngagement ? (
                      <div className="p-8 text-center text-slate-400">Loading...</div>
                    ) : engagement.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No engagement data yet.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-800 text-slate-400">
                              <th className="text-left px-4 py-2 font-medium">Date</th>
                              <th className="text-left px-4 py-2 font-medium">Active Students</th>
                              <th className="text-left px-4 py-2 font-medium">Events</th>
                              <th className="text-left px-4 py-2 font-medium">Credits Earned</th>
                            </tr>
                          </thead>
                          <tbody>
                            {engagement.map((e, i) => (
                              <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                                <td className="px-4 py-2">{new Date(e.day).toLocaleDateString()}</td>
                                <td className="px-4 py-2">{e.active_students}</td>
                                <td className="px-4 py-2">{e.events}</td>
                                <td className="px-4 py-2 text-yellow-400">{e.total_credits ?? 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Credit trends */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800">
                      <h3 className="font-semibold">Credit Economy (Last 30 Days)</h3>
                    </div>
                    {creditTrends.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No credit data yet.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-800 text-slate-400">
                              <th className="text-left px-4 py-2 font-medium">Date</th>
                              <th className="text-left px-4 py-2 font-medium">Earned</th>
                              <th className="text-left px-4 py-2 font-medium">Spent</th>
                              <th className="text-left px-4 py-2 font-medium">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {creditTrends.map((c, i) => {
                              const net = (c.earned ?? 0) - (c.spent ?? 0);
                              return (
                                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                                  <td className="px-4 py-2">{new Date(c.day).toLocaleDateString()}</td>
                                  <td className="px-4 py-2 text-green-400">+{c.earned ?? 0}</td>
                                  <td className="px-4 py-2 text-red-400">-{c.spent ?? 0}</td>
                                  <td className={`px-4 py-2 font-mono ${net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {net >= 0 ? '+' : ''}{net}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Events tab */}
              {activeTab === 'events' && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      <h3 className="font-semibold">Assessment Events</h3>
                    </div>
                    <span className="text-xs text-slate-500">Last 30 days</span>
                  </div>
                  {loadingEvents ? (
                    <div className="p-8 text-center text-slate-400">Loading events...</div>
                  ) : events.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">No assessment events for this class yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-800 text-slate-400">
                            <th className="text-left px-4 py-2 font-medium">Time</th>
                            <th className="text-left px-4 py-2 font-medium">Student</th>
                            <th className="text-left px-4 py-2 font-medium">Subject</th>
                            <th className="text-left px-4 py-2 font-medium">Topic</th>
                            <th className="text-left px-4 py-2 font-medium">Type</th>
                            <th className="text-left px-4 py-2 font-medium">Marks</th>
                            <th className="text-left px-4 py-2 font-medium">Credits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((e) => (
                            <tr key={e.event_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                              <td className="px-4 py-2 text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</td>
                              <td className="px-4 py-2">{e.student_name || '—'}</td>
                              <td className="px-4 py-2">{e.subject || '—'}</td>
                              <td className="px-4 py-2">{e.topic || '—'}</td>
                              <td className="px-4 py-2 text-xs">{e.question_type || '—'}</td>
                              <td className="px-4 py-2">
                                {e.marks_awarded != null && e.max_marks != null
                                  ? `${e.marks_awarded}/${e.max_marks}`
                                  : '—'}
                              </td>
                              <td className="px-4 py-2 text-yellow-400">{e.credits_awarded}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {events.length >= eventLimit && (
                        <div className="p-3 text-center">
                          <button
                            onClick={() => setEventLimit((l) => l + 50)}
                            className="text-sm text-cyan-400 hover:text-cyan-300"
                          >
                            Load more
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Flagged tab */}
              {activeTab === 'flagged' && (
                <div className="space-y-6">
                  {/* Credit spikes */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <h3 className="font-semibold">Credit Spikes (&gt;200 cr/hour)</h3>
                    </div>
                    {loadingFlagged ? (
                      <div className="p-8 text-center text-slate-400">Loading...</div>
                    ) : !flagged || flagged.spikes.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No credit spikes detected.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-800 text-slate-400">
                              <th className="text-left px-4 py-2 font-medium">Student</th>
                              <th className="text-left px-4 py-2 font-medium">Hour</th>
                              <th className="text-left px-4 py-2 font-medium">Credits</th>
                              <th className="text-left px-4 py-2 font-medium">Events</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flagged.spikes.map((s, i) => (
                              <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                                <td className="px-4 py-2">{s.student_name || '—'}</td>
                                <td className="px-4 py-2 text-xs text-slate-400">{new Date(s.hour_window).toLocaleString()}</td>
                                <td className="px-4 py-2 text-yellow-400 font-mono">{s.total_credits ?? 0}</td>
                                <td className="px-4 py-2">{s.event_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Repeated questions */}
                  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <h3 className="font-semibold">Repeated Questions (&gt;3 attempts)</h3>
                    </div>
                    {loadingFlagged ? (
                      <div className="p-8 text-center text-slate-400">Loading...</div>
                    ) : !flagged || flagged.repeated.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No repeated questions detected.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-800 text-slate-400">
                              <th className="text-left px-4 py-2 font-medium">Student</th>
                              <th className="text-left px-4 py-2 font-medium">Question</th>
                              <th className="text-left px-4 py-2 font-medium">Topic</th>
                              <th className="text-left px-4 py-2 font-medium">Attempts</th>
                              <th className="text-left px-4 py-2 font-medium">Last Attempt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flagged.repeated.map((r, i) => (
                              <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                                <td className="px-4 py-2">{r.student_name || '—'}</td>
                                <td className="px-4 py-2 text-xs font-mono">{r.question_id || '—'}</td>
                                <td className="px-4 py-2">{r.topic || '—'}</td>
                                <td className="px-4 py-2">{r.attempt_count}</td>
                                <td className="px-4 py-2 text-xs text-slate-400">{new Date(r.last_attempt).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Exports tab */}
              {activeTab === 'exports' && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <h3 className="font-semibold mb-6 flex items-center gap-2">
                    <Download className="w-5 h-5 text-cyan-400" />
                    Data Export
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                      <h4 className="font-medium mb-2">Engagement Report</h4>
                      <p className="text-sm text-slate-400 mb-4">
                        Student names, planet stages, credit balances, upgrade counts, and last activity.
                      </p>
                      <button
                        onClick={handleExportEngagement}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download CSV
                      </button>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                      <h4 className="font-medium mb-2">Assessment Events Report</h4>
                      <p className="text-sm text-slate-400 mb-4">
                        All assessment events with subjects, topics, marks, time spent, and credits awarded.
                      </p>
                      <button
                        onClick={handleExportAssessments}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download CSV
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
